import { Component } from '@angular/core';
import { FilterDrawerFieldConfig, FilterDrawerValue } from '../../../components/filter-drawer/filter-drawer.models';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import alertify from 'alertifyjs';
import { CommonModule } from '@angular/common';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { FilterDrawerComponent } from '../../../components/filter-drawer/filter-drawer.component';
import { PaymentResponse, PaymentRow } from './search-payments.model';
import { GenericListResponse, GenericPaginationResponse, GenericPaginationResponseV2 } from '../../../modules/shared/models/general.modal';
import { CategoryOption, MdaListResponse, MdaOption } from '../../../modules/services/utility-service.models';
import { DropdownMenu } from "../../../components/dropdown-menu/dropdown-menu.component";

type PaymentStatusFilter = '' | 'PAID' | 'PENDING';

type PaymentFilter = {
  reference: string;
  paymentName: string;
  mdaId: string;
  categoryId: string;
  amount: string;
  paymentStatus: PaymentStatusFilter;
};

@Component({
  selector: 'app-search-payments',
  imports: [CommonModule, PageHeaderComponent, FilterDrawerComponent, DropdownMenu],
  templateUrl: './search-payments.html',
  styleUrl: './search-payments.scss',
})
export class SearchVendorPayments {
  readonly columns = [
    'Date',
    'Reference',
    'Payer Name',
    'MDA',
    'Category',
    'Amount',
    'Status',
    'Actions',
  ];

  readonly pageSize = 10;
  payments: PaymentRow[] = [];
  allpayments: PaymentRow[] = [];
  isLoadingpayments = false;
  openMenuIndex: number | null = null;

  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  readonly emptyFilters: PaymentFilter = {
    reference: '',
    paymentName: '',
    amount: '',
    categoryId: '',
    mdaId: '',
    paymentStatus: '',
  };

  paymentFilters: PaymentFilter = { ...this.emptyFilters };
  paymentFilterFields: FilterDrawerFieldConfig[] = [];
  isFilterDrawerOpen = false;

  isDetailsModalOpen = false;
  selectedpayment: PaymentRow | null = null;
  isLoadingpaymentDetails = false;
  isLoadingPayments = false;

  mdaOptions: MdaOption[] = [];
  categoryOptions: CategoryOption[] = [];

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.fetchPayments();
    this.getMdas();
    this.getCategories();
  }

  getMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];
        this.buildVendorFilterFields();
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(error);
        }
      },
    });
  }


  getCategories(): void {
    this.apiService.get('/categories').subscribe({
      next: (response: GenericListResponse<CategoryOption>) => {
        if ("content" in response.data) {
          this.categoryOptions = response.data.content ?? []
          this.buildVendorFilterFields();
          return;
        };
        this.categoryOptions = response.data ?? [];
        this.buildVendorFilterFields();
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(error);
        }
      },
    });
  }

  private fetchPayments(page = 1): void {
    this.isLoadingPayments = true;
    this.openMenuIndex = null;

    this.apiService.post('/payment/search', 
      {
        categoryId: this.paymentFilters.categoryId || undefined,
        fromDate: undefined,
        mdaId: this.paymentFilters.mdaId || undefined,
        page: page,
        pageSize: 10,
        paymentChannel: undefined,
        paymentReference: this.paymentFilters.reference || undefined,
        paymentStatus: this.paymentFilters.paymentStatus || undefined,
        toDate: undefined
      }
    ).subscribe({
      next: (response: GenericPaginationResponseV2<PaymentResponse>) => {
        if (response.status !== "success") throw new Error(response.message || "Failed to get payments");
        this.payments = this.mapPaymentRow(response.data.content);
        this.totalItems = response.data.total ?? this.payments.length;
        this.totalPages = (Math.ceil(response.data.total / this.pageSize)) ?? 1;
        this.currentPage = response.data.page;
        this.hasNextPage = response.data.page < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.isLoadingPayments = false;
      },
      error: (error) => {
        this.allpayments = [];
        this.payments = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.currentPage = 1;
        this.hasPreviousPage = false;
        this.hasNextPage = false;
        
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(error || "Unable to fetch payments. Please try again.");
        }
        this.isLoadingPayments = false;
      }
    });
  }

  private openDownloadUrl(url: string, label: string): void {
    if (!url) {
      alertify.error(`Unable to start ${label} download.`);
      return;
    }

    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) {
      alertify.message(`${label} opened in this tab because the browser blocked a new tab.`);
      return;
    }
  }

  handleReceiptDownload(vendorId: string): void {
    if (!String(vendorId ?? '').trim()) {
      alertify.error('Unable to start payment receipt download.');
      return;
    }

    this.openDownloadUrl(this.apiService.getReceiptDownloadUrl(vendorId), 'payment receipt');
  }

  mapPaymentRow(paymentResponse: PaymentResponse[]): PaymentRow[] {
    return paymentResponse.map<PaymentRow>(x => {
      return {...x}
    })
  }

  openFilters(): void {
    this.openMenuIndex = null;
    this.isFilterDrawerOpen = true;
  }

  closeFilters(): void {
    this.isFilterDrawerOpen = false;
  }

  get activeFilterCount(): number {
    return Object.entries(this.paymentFilters).filter(([key, value]) => {
      if (key === 'status') return value !== 'ALL';
      return String(value ?? '').trim().length > 0;
    }).length;
  }

  getStatusPillClass(status: string): string {
    const normalized = String(status ?? '').toUpperCase();

    if (normalized === 'PAID') {
      return 'bg-green-50 text-green-700 border !border-green-200';
    }

    if (normalized === 'PENDING') {
      return 'bg-amber-50 text-amber-700 border !border-amber-200';
    }

    return 'bg-slate-50 text-slate-600 border border-slate-200';
  }

  toggleMenu(index: number): void {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  downloadDoc(payment: PaymentRow) {}

  goToPreviousPage(): void {
    if (!this.hasPreviousPage || this.isLoadingPayments) return;
    this.fetchPayments(this.currentPage - 1);
  }

  goToNextPage(): void {
    if (!this.hasNextPage || this.isLoadingPayments) return;
    this.fetchPayments(this.currentPage + 1);
  }

  applyFilters(raw: FilterDrawerValue): void {
    this.paymentFilters = {
      ...this.emptyFilters,
      ...Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [key, `${value ?? ''}`]),
      ),
    } as PaymentFilter;

    // const rawStatus = (raw['status'] ?? 'ALL') as string;
    // this.paymentFilters.status = (rawStatus as PaymentStatusFilter) || '';

    this.isFilterDrawerOpen = false;
    this.fetchPayments(1);
  }

  private buildVendorFilterFields(): void {
    const mdaOptions = Array.from(
      new Map(
        this.mdaOptions.map((mda) => [
          mda.name,
          { label: mda.name, value: mda.name },
        ]),
      ).values(),
    );

    const categoryOptions = Array.from(
      new Map(
        this.categoryOptions.map((category) => [
          category.name,
          { label: category.name, value: category.id },
        ]),
      ).values(),
    );

    const paymentStatusOptions = Array.from(
      new Map(
        ["", "PAID", "PENDING"].map((status) => [
          status,
          { label: status, value: status },
        ]),
      ).values(),
    )

    this.paymentFilterFields = [
      {
        key: 'reference',
        label: 'Reference',
        type: 'text',
        placeholder: 'Filter by reference',
      },
      {
        key: 'payerName',
        label: 'Payer Name',
        type: 'text',
        placeholder: 'Filter by payer name',
      },
      {
        key: 'mdaName',
        label: 'MDA',
        type: 'searchable-select',
        placeholder: 'Select MDA',
        searchablePlaceholder: 'Search MDA options',
        options: mdaOptions,
      },
      {
        key: 'categoryId',
        label: 'Category',
        type: 'searchable-select',
        placeholder: 'Select category',
        searchablePlaceholder: 'Search category options',
        options: categoryOptions,
      },
      {
        key: 'paymentStatus',
        label: 'Payment Status',
        type: 'searchable-select',
        placeholder: 'Select payment status',
        searchablePlaceholder: 'Search category options',
        options: paymentStatusOptions,
      },
    ];
  }
}
